/**
 * IKEA URL Builders
 * Constructs URLs for IKEA As-Is, Clearance, and Product pages
 */

// US store ID to slug mapping (mirrors seed-worker/src/scraper.ts)
const US_STORE_SLUGS: Record<string, string> = {
  // Midwest
  '004': 'schaumburg',
  '005': 'bolingbrook',
  '007': 'bloomington',
  '141': 'canton',
  '145': 'columbus',
  '148': 'oak-creek',
  '160': 'merriam',
  '162': 'west-chester',
  '144': 'st-louis',
  // Northeast
  '008': 'conshohocken',
  '009': 'college-park',
  '146': 'pittsburgh',
  '152': 'white-marsh',
  '050': 'stoughton',
  '136': 'brooklyn',
  '142': 'new-haven',
  // Southeast
  '014': 'atlanta',
  '147': 'louisville',
  '150': 'memphis',
  '151': 'norfolk',
  '164': 'cary',
  '239': 'charlotte',
  '387': 'jacksonville',
  '388': 'miami',
  '389': 'orlando',
  '390': 'tampa',
  // South
  '017': 'houston',
  '149': 'grand-prairie',
  '153': 'live-oak',
  '018': 'frisco',
  // West
  '154': 'tempe',
  '155': 'las-vegas',
  '156': 'centennial',
  '065': 'portland',
  '067': 'renton',
  '083': 'burbank',
  '085': 'carson',
  '086': 'costa-mesa',
  '087': 'covina',
  '088': 'east-palo-alto',
  '089': 'emeryville',
  '090': 'san-diego',
};

// Countries with special domains (not ikea.com)
// Format: { domain, langPrefix } - langPrefix replaces the standard /{lang}/ path
const SPECIAL_DOMAINS: Record<string, { domain: string; langPrefix?: string }> = {
  ID: { domain: 'www.ikea.co.id' },           // Indonesia uses ikea.co.id
  CN: { domain: 'www.ikea.cn' },              // China uses ikea.cn
  GR: { domain: 'www.ikea.gr' },              // Greece uses ikea.gr
  HK: { domain: 'www.ikea.com.hk' },          // Hong Kong uses ikea.com.hk
  TW: { domain: 'www.ikea.com.tw' },          // Taiwan uses ikea.com.tw
  PR: { domain: 'www.ikea.pr', langPrefix: 'puertorico/en' },  // Puerto Rico uses ikea.pr/puertorico/en
  DO: { domain: 'www.ikea.com.do', langPrefix: 'es' },  // Dominican Republic uses ikea.com.do/es
};

// Country to language code mapping
const COUNTRY_LANGS: Record<string, string> = {
  // North America
  US: 'en',
  CA: 'en',
  // Europe - Western
  GB: 'en',
  IE: 'en',
  DE: 'de',
  AT: 'de',
  CH: 'de',
  FR: 'fr',
  BE: 'nl',
  NL: 'nl',
  // Europe - Southern
  ES: 'es',
  IT: 'it',
  PT: 'pt',
  GR: 'el',
  // Europe - Nordic
  SE: 'sv',
  NO: 'no',
  DK: 'da',
  FI: 'fi',
  // Europe - Central/Eastern
  PL: 'pl',
  CZ: 'cs',
  HU: 'hu',
  SK: 'sk',
  RO: 'ro',
  SI: 'sl',
  HR: 'hr',
  RS: 'sr',
  // Asia Pacific
  AU: 'en',
  JP: 'ja',
  KR: 'ko',
  CN: 'zh',
  TW: 'en',
  HK: 'en',
  SG: 'en',
  MY: 'ms',
  TH: 'th',
  ID: 'id',
  PH: 'en',
  IN: 'en',
  // Middle East
  AE: 'en',
  SA: 'ar',
  QA: 'en',
  KW: 'en',
  BH: 'en',
  JO: 'ar',
  IL: 'he',
  // Africa
  EG: 'ar',
  MA: 'fr',
  // Americas - Latin
  MX: 'es',
  DO: 'es',
  PR: 'es',
  CL: 'es',
};

/**
 * Build As-Is page URL for a store
 */
export function buildAsIsUrl(storeId: string, country: string, slug: string | null): string {
  if (country === 'US') {
    // Prefer database slug, fallback to hardcoded lookup, then storeId
    const storeSlug = slug || US_STORE_SLUGS[storeId] || storeId;
    return `https://www.ikea.com/us/en/customer-service/shopping-at-ikea/as-is-online-pubce1eedc0/#/${storeSlug}`;
  }

  const storeSlug = slug || storeId;

  // Handle countries with special domains
  const specialConfig = SPECIAL_DOMAINS[country];
  if (specialConfig) {
    const langPath = specialConfig.langPrefix || COUNTRY_LANGS[country] || 'en';
    return `https://${specialConfig.domain}/${langPath}/circular/second-hand/#/${storeSlug}`;
  }

  const lang = COUNTRY_LANGS[country] || 'en';
  return `https://www.ikea.com/${country.toLowerCase()}/${lang}/circular/second-hand/#/${storeSlug}`;
}

/**
 * Build Clearance page URL for a country
 * @deprecated Use buildSalePageUrl(country, 'last_chance') instead
 */
export function buildClearanceUrl(country: string): string {
  return buildSalePageUrl(country, 'last_chance');
}

type SaleType = 'last_chance' | 'family_offers' | 'limited_time';

const SALE_PAGE_PATHS: Record<SaleType, string> = {
  last_chance: 'cat/last-chance',
  family_offers: 'offers/family-offers',
  limited_time: 'offers/limited-time-offers',
};

/**
 * Build sale page URL for a country
 */
export function buildSalePageUrl(country: string, saleType: SaleType): string {
  const path = SALE_PAGE_PATHS[saleType];

  const specialConfig = SPECIAL_DOMAINS[country];
  if (specialConfig) {
    const langPath = specialConfig.langPrefix || COUNTRY_LANGS[country] || 'en';
    return `https://${specialConfig.domain}/${langPath}/${path}/`;
  }

  const lang = COUNTRY_LANGS[country] || 'en';
  return `https://www.ikea.com/${country.toLowerCase()}/${lang}/${path}/`;
}

/**
 * Build product page URL
 */
export function buildProductUrl(productCode: string, country: string): string {
  // Remove dots from product code: 123.456.78 -> 12345678
  const code = productCode.replace(/\./g, '');

  const specialConfig = SPECIAL_DOMAINS[country];
  if (specialConfig) {
    const langPath = specialConfig.langPrefix || COUNTRY_LANGS[country] || 'en';
    return `https://${specialConfig.domain}/${langPath}/p/${code}/`;
  }

  const lang = COUNTRY_LANGS[country] || 'en';
  return `https://www.ikea.com/${country.toLowerCase()}/${lang}/p/${code}/`;
}
